import { useState, useCallback, useMemo, useEffect } from "react";
import { format, parseISO } from "date-fns";
import { getBusinessToday } from "@/utils/businessTime";
import { fr } from "date-fns/locale";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  Chip,
  InputBase,
  Checkbox,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { Assessment, Restaurant, Inventory, Science, LocalShipping, Search } from "@mui/icons-material";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { LoadingSpinner, PageHeader, Button, DateRangeFilter, FiltersCard, InputField } from "@/components/ui";
import { estimationService, type EstimationByProductZone, type ProductType } from "@/services/estimation.service";
import { formatQuantityWithConversion } from "@/constants/measurementUnits";
import { useToastStore } from "@/store/toastStore";
import { useAuthStore } from "@/store/authStore";
import { useDriverPlanningUpdates } from "@/hooks/useDriverPlanningUpdates";

interface EstimationsSessionState {
  localEstimates: Record<string, number>;
  checkedZoneQuantities: Record<string, number>;
}

function toNumericRecord(value: unknown): Record<string, number> {
  if (!value || typeof value !== "object") return {};
  const result: Record<string, number> = {};
  for (const [key, raw] of Object.entries(value as Record<string, unknown>)) {
    const num = Number(raw);
    if (Number.isFinite(num)) result[key] = num;
  }
  return result;
}

function getTodayStr(): string {
  return getBusinessToday();
}

/** Valeur numérique effective (local ou backend). */
function getEstimatedNumeric(
  productId: string,
  rowEstimated: number,
  local: Record<string, number>
): number {
  return productId in local ? local[productId]! : rowEstimated;
}

/** Affichage champ entier : pas de « 0 » visible par défaut (placeholder à la place). */
function formatIntInputValue(n: number): string {
  return n === 0 ? "" : String(n);
}

type ProductScopeFilter = "all" | "ordered" | "estimated" | "gap";

interface EstimationProductRow {
  productId: string;
  productName: string;
  type: ProductType;
  typeLabel: string;
  estimated: number;
  real: number;
}

function matchesProductSearch(
  row: EstimationProductRow,
  zoneRows: EstimationByProductZone[],
  normalizedSearch: string,
): boolean {
  if (!normalizedSearch) return true;
  if (row.productName.toLowerCase().includes(normalizedSearch)) return true;
  return zoneRows.some((zone) => zone.zoneName.toLowerCase().includes(normalizedSearch));
}

export function EstimationsPage(): JSX.Element {
  const [dateFrom, setDateFrom] = useState(() => getTodayStr());
  const [dateTo, setDateTo] = useState(() => getTodayStr());
  const [localEstimates, setLocalEstimates] = useState<Record<string, number>>({});
  /** DRIVER : snapshot des lignes cochées ("productId::zoneId" -> quantité au moment du check) */
  const [checkedZoneQuantities, setCheckedZoneQuantities] = useState<Record<string, number>>({});
  const [planningChangesWarningCount, setPlanningChangesWarningCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState<ProductType | "all">("all");
  const [productScopeFilter, setProductScopeFilter] = useState<ProductScopeFilter>("all");
  const queryClient = useQueryClient();
  const { success, error: showError } = useToastStore();
  const user = useAuthStore((s) => s.user);
  const isDriver = user?.role === "DRIVER";
  const sessionStorageKey = useMemo(
    () => `estimations:${isDriver ? "driver" : "backoffice"}:${dateFrom}:${dateTo}`,
    [isDriver, dateFrom, dateTo]
  );

  const {
    data: estimations,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ["estimations", dateFrom, dateTo],
    queryFn: () => estimationService.getEstimations(dateFrom, dateTo),
    enabled: !!dateFrom && !!dateTo,
    // Driver: rafraîchissement périodique pour capter aussi les changements externes
    refetchInterval: isDriver ? 15000 : false,
  });

  useDriverPlanningUpdates(() => {
    void refetch();
  });

  const updateEstimationMutation = useMutation({
    mutationFn: ({
      productId,
      estimated,
    }: {
      productId: string;
      estimated: number;
    }) =>
      estimationService.updateProductEstimation(dateFrom, dateTo, productId, estimated),
    onSuccess: (_, { productId }) => {
      setLocalEstimates((prev) => {
        const next = { ...prev };
        delete next[productId];
        return next;
      });
      queryClient.invalidateQueries({ queryKey: ["estimations", dateFrom, dateTo] });
      success("Estimation enregistrée");
    },
    onError: (err: Error) => {
      showError(err.message || "Erreur lors de l'enregistrement");
    },
  });

  const handleSaveEstimated = useCallback(
    (productId: string, value: number) => {
      const num = Math.max(0, Math.floor(Number(value)));
      updateEstimationMutation.mutate({ productId, estimated: num });
    },
    [updateEstimationMutation]
  );

  const productZoneRowsByProduct = useMemo(() => {
    if (!estimations) return new Map<string, EstimationByProductZone[]>();
    const map = new Map<string, EstimationByProductZone[]>();
    for (const row of estimations.byProductZone) {
      const current = map.get(row.productId) ?? [];
      current.push(row);
      map.set(row.productId, current);
    }
    return map;
  }, [estimations]);

  const zoneQuantityByKey = useMemo(() => {
    const map = new Map<string, number>();
    if (!estimations) return map;
    for (const row of estimations.byProductZone) {
      map.set(`${row.productId}::${row.zoneId}`, row.totalQuantity);
    }
    return map;
  }, [estimations]);

  const readyProductZoneKeys = useMemo(
    () => new Set(Object.keys(checkedZoneQuantities)),
    [checkedZoneQuantities]
  );

  const driverProductIds = useMemo(() => {
    if (!isDriver || !estimations) return [];
    return estimations.byProduct.filter((p) => p.totalQuantity > 0).map((p) => p.productId);
  }, [isDriver, estimations]);

  const allDriverReady = useMemo(() => {
    if (!isDriver || !estimations || driverProductIds.length === 0) return false;
    return driverProductIds.every((productId) => {
      const zoneRows = productZoneRowsByProduct.get(productId) ?? [];
      if (zoneRows.length === 0) return false;
      return zoneRows.every((z) => readyProductZoneKeys.has(`${z.productId}::${z.zoneId}`));
    });
  }, [isDriver, estimations, driverProductIds, productZoneRowsByProduct, readyProductZoneKeys]);

  const normalizedSearch = searchQuery.trim().toLowerCase();

  const productRows = useMemo((): EstimationProductRow[] => {
    if (!estimations) return [];
    const allProductIds = new Set([
      ...estimations.byProduct.map((p) => p.productId),
      ...(isDriver ? [] : (estimations.estimatedByProduct ?? []).map((p) => p.productId)),
    ]);
    return Array.from(allProductIds).map((productId) => {
      const real = estimations.byProduct.find((p) => p.productId === productId);
      const est = estimations.estimatedByProduct?.find((p) => p.productId === productId);
      return {
        productId,
        productName: real?.productName ?? est?.productName ?? "",
        type: real?.type ?? est?.type ?? "MAIN_COURSE",
        typeLabel: real?.typeLabel ?? est?.typeLabel ?? "",
        estimated: est?.estimated ?? 0,
        real: real?.totalQuantity ?? 0,
      };
    });
  }, [estimations, isDriver]);

  const filteredProductRows = useMemo(() => {
    const rows = productRows.filter((row) => {
      if (isDriver && row.real <= 0) return false;
      if (typeFilter !== "all" && row.type !== typeFilter) return false;

      const zoneRows = productZoneRowsByProduct.get(row.productId) ?? [];
      if (!matchesProductSearch(row, zoneRows, normalizedSearch)) return false;

      const estimatedNumeric = getEstimatedNumeric(row.productId, row.estimated, localEstimates);
      const diff = row.real - estimatedNumeric;

      if (productScopeFilter === "ordered" && row.real <= 0) return false;
      if (productScopeFilter === "estimated" && estimatedNumeric <= 0) return false;
      if (productScopeFilter === "gap" && diff === 0) return false;

      return true;
    });
    rows.sort((a, b) => a.typeLabel.localeCompare(b.typeLabel) || a.productName.localeCompare(b.productName));
    return rows;
  }, [
    productRows,
    isDriver,
    typeFilter,
    normalizedSearch,
    productScopeFilter,
    productZoneRowsByProduct,
    localEstimates,
  ]);

  const visibleTypeRows = useMemo(() => {
    if (!estimations) return [];
    const baseRows = isDriver ? estimations.byType.filter((row) => row.count > 0) : estimations.byType;
    const visibleTypes = new Set(filteredProductRows.map((row) => row.type));
    return baseRows.filter((row) => {
      if (typeFilter !== "all" && row.type !== typeFilter) return false;
      if (normalizedSearch || productScopeFilter !== "all") {
        return visibleTypes.has(row.type);
      }
      return true;
    });
  }, [estimations, isDriver, typeFilter, normalizedSearch, productScopeFilter, filteredProductRows]);

  const filteredIngredients = useMemo(() => {
    if (!estimations) return [];
    return estimations.byIngredient.filter(
      (row) =>
        !normalizedSearch || row.ingredientName.toLowerCase().includes(normalizedSearch),
    );
  }, [estimations, normalizedSearch]);

  const hasActiveFilters =
    normalizedSearch.length > 0 || typeFilter !== "all" || productScopeFilter !== "all";

  // Restaurer l'état de session (coches + quantités saisies) à chaque changement de période
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const raw = window.sessionStorage.getItem(sessionStorageKey);
      if (!raw) {
        setLocalEstimates({});
        setCheckedZoneQuantities({});
        setPlanningChangesWarningCount(0);
        return;
      }
      const parsed = JSON.parse(raw) as Partial<EstimationsSessionState>;
      setLocalEstimates(toNumericRecord(parsed.localEstimates));
      setCheckedZoneQuantities(toNumericRecord(parsed.checkedZoneQuantities));
      setPlanningChangesWarningCount(0);
    } catch {
      setLocalEstimates({});
      setCheckedZoneQuantities({});
      setPlanningChangesWarningCount(0);
    }
  }, [sessionStorageKey]);

  // Persister l'état de session
  useEffect(() => {
    if (typeof window === "undefined") return;
    const payload: EstimationsSessionState = {
      localEstimates,
      checkedZoneQuantities,
    };
    window.sessionStorage.setItem(sessionStorageKey, JSON.stringify(payload));
  }, [sessionStorageKey, localEstimates, checkedZoneQuantities]);

  // Si les quantités backend changent, décocher automatiquement les sous-lignes non concordantes
  useEffect(() => {
    if (!estimations) return;
    let removedCount = 0;
    const next: Record<string, number> = {};
    for (const [key, savedQty] of Object.entries(checkedZoneQuantities)) {
      const currentQty = zoneQuantityByKey.get(key);
      if (currentQty !== undefined && currentQty === savedQty) {
        next[key] = savedQty;
      } else {
        removedCount += 1;
      }
    }
    if (removedCount > 0) {
      setCheckedZoneQuantities(next);
      setPlanningChangesWarningCount(removedCount);
    }
  }, [estimations, zoneQuantityByKey, checkedZoneQuantities]);

  const toggleDriverReadyForZone = useCallback((productId: string, zoneId: string) => {
    const key = `${productId}::${zoneId}`;
    const currentQty = zoneQuantityByKey.get(key);
    if (currentQty === undefined) return;
    setCheckedZoneQuantities((prev) => {
      const next = { ...prev };
      if (key in next) delete next[key];
      else next[key] = currentQty;
      return next;
    });
  }, [zoneQuantityByKey]);

  const toggleDriverReadyForProduct = useCallback(
    (productId: string) => {
      const zoneRows = productZoneRowsByProduct.get(productId) ?? [];
      if (zoneRows.length === 0) return;
      const zoneEntries = zoneRows
        .map((z) => ({
          key: `${z.productId}::${z.zoneId}`,
          qty: zoneQuantityByKey.get(`${z.productId}::${z.zoneId}`),
        }))
        .filter((e): e is { key: string; qty: number } => e.qty !== undefined);
      setCheckedZoneQuantities((prev) => {
        const next = { ...prev };
        const allChecked = zoneEntries.every((e) => e.key in next);
        if (allChecked) {
          zoneEntries.forEach((e) => {
            delete next[e.key];
          });
        } else {
          zoneEntries.forEach((e) => {
            next[e.key] = e.qty;
          });
        }
        return next;
      });
    },
    [productZoneRowsByProduct, zoneQuantityByKey]
  );

  return (
    <Box sx={{ p: 2 }}>
      <PageHeader
        title="Estimations"
        subtitle="Quantités par type de plat, produits commandés et ingrédients nécessaires sur une période"
        titleIcon={<Assessment />}
      />

      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
          Période
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm="auto">
            <DateRangeFilter
              dateFrom={dateFrom}
              dateTo={dateTo}
              onChangeFrom={setDateFrom}
              onChangeTo={setDateTo}
              showNavArrows
              showTodayButton
              showClearButton={false}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm="auto">
            <Button variant="primary" onClick={() => refetch()} disabled={isLoading}>
              Actualiser
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {estimations && !isLoading && (
        <Box sx={{ mb: 3 }}>
          <FiltersCard>
          <InputField
            placeholder="Rechercher un produit, une zone ou un ingrédient…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="small"
            icon={<Search />}
            iconPosition="start"
            sx={{ flexGrow: 1, minWidth: 250, "& .MuiOutlinedInput-root": { height: 40 } }}
          />
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Type de plat</InputLabel>
            <Select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value as ProductType | "all")}
              label="Type de plat"
            >
              <MenuItem value="all">Tous</MenuItem>
              <MenuItem value="STARTER">Entrée</MenuItem>
              <MenuItem value="MAIN_COURSE">Plat</MenuItem>
              <MenuItem value="DESSERT">Dessert</MenuItem>
              <MenuItem value="DRINK">Boisson</MenuItem>
              <MenuItem value="SIDE">Accompagnement / 100 % légumes</MenuItem>
            </Select>
          </FormControl>
          {!isDriver && (
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Produits</InputLabel>
              <Select
                value={productScopeFilter}
                onChange={(e) => setProductScopeFilter(e.target.value as ProductScopeFilter)}
                label="Produits"
              >
                <MenuItem value="all">Tous</MenuItem>
                <MenuItem value="ordered">Commandés uniquement</MenuItem>
                <MenuItem value="estimated">Estimés uniquement</MenuItem>
                <MenuItem value="gap">Avec écart estimé / réel</MenuItem>
              </Select>
            </FormControl>
          )}
          {hasActiveFilters ? (
            <Button variant="outline" onClick={() => {
              setSearchQuery("");
              setTypeFilter("all");
              setProductScopeFilter("all");
            }}>
              Réinitialiser
            </Button>
          ) : null}
          </FiltersCard>
        </Box>
      )}

      {isLoading && <LoadingSpinner />}
      {isError && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error instanceof Error ? error.message : "Erreur lors du chargement des estimations."}
        </Typography>
      )}

      {estimations && !isLoading && (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Période du{" "}
            {format(parseISO(dateFrom), "d MMMM yyyy", { locale: fr })} au{" "}
            {format(parseISO(dateTo), "d MMMM yyyy", { locale: fr })} — commandes
            livrées ou à livrer (hors annulées).
          </Typography>

          {estimations.estimatedByProduct.length === 0 && estimations.byProduct.length === 0 && (
            <Box sx={{ mb: 3, p: 2, bgcolor: "warning.light", borderRadius: 1 }}>
              <Typography variant="body2" fontWeight={600} color="warning.dark">
                Aucune donnée pour cette période
              </Typography>
              <Typography variant="body2" color="warning.dark" sx={{ mt: 0.5 }}>
                • Aucun menu avec portions estimées<br />
                • Aucune commande enregistrée
              </Typography>
            </Box>
          )}

          {estimations.estimatedByProduct.length === 0 && estimations.byProduct.length > 0 && (
            <Box sx={{ mb: 3, p: 2, bgcolor: "info.light", borderRadius: 1 }}>
              <Typography variant="body2" fontWeight={600} color="info.dark">
                Pas de portions estimées dans les menus pour cette période
              </Typography>
              <Typography variant="body2" color="info.dark" sx={{ mt: 0.5 }}>
                Allez sur la page <strong>Menus</strong> pour renseigner les champs "Est. portions" dans les créneaux.
              </Typography>
            </Box>
          )}

          {/* Résumé par type */}
          <Typography variant="h6" sx={{ mb: 1.5, display: "flex", alignItems: "center", gap: 1 }}>
            <Restaurant /> Quantités par type de plat
          </Typography>
          <TableContainer component={Paper} sx={{ mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Type</TableCell>
                  {!isDriver && (
                    <TableCell align="right" sx={{ bgcolor: "primary.main", color: "primary.contrastText" }}>Estimé (menu)</TableCell>
                  )}
                  <TableCell align="right" sx={{ bgcolor: "success.main", color: "success.contrastText" }}>Réel (commandé)</TableCell>
                  {!isDriver && <TableCell align="right">Écart</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleTypeRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={isDriver ? 2 : 4} align="center" sx={{ py: 3 }}>
                      {hasActiveFilters
                        ? "Aucun type ne correspond aux filtres."
                        : isDriver
                          ? "Aucune commande sur cette période."
                          : "Aucune donnée sur cette période."}
                    </TableCell>
                  </TableRow>
                ) : (
                  visibleTypeRows.map((row) => {
                    const estimated = estimations.estimatedByType?.find((e) => e.type === row.type)?.estimated ?? 0;
                    const real = row.count;
                    const diff = real - estimated;
                    return (
                      <TableRow key={row.type}>
                        <TableCell>{row.label}</TableCell>
                        {!isDriver && (
                          <TableCell align="right" sx={{ fontWeight: 600, color: "primary.main" }}>{estimated}</TableCell>
                        )}
                        <TableCell align="right" sx={{ fontWeight: 600, color: "success.main" }}>{real}</TableCell>
                        {!isDriver && (
                          <TableCell align="right" sx={{ fontWeight: 600, color: diff >= 0 ? "success.main" : "error.main" }}>
                            {diff > 0 ? `+${diff}` : diff}
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Produits commandés */}
          <Typography variant="h6" sx={{ mb: 1.5, display: "flex", alignItems: "center", gap: 1 }}>
            <Inventory /> {isDriver ? "Produits commandés" : "Produits : Estimé vs Réel"}
            {hasActiveFilters ? (
              <Chip
                size="small"
                label={`${filteredProductRows.length} affiché${filteredProductRows.length > 1 ? "s" : ""}`}
                variant="outlined"
              />
            ) : null}
          </Typography>
          {isDriver && allDriverReady && (
            <Alert severity="success" icon={<LocalShipping />} sx={{ mb: 2 }}>
              <strong>Vous avez tout préparé.</strong> Vous pouvez passer aux livraisons.
            </Alert>
          )}
          {isDriver && planningChangesWarningCount > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Des changements de planning/quantités ont été détectés. {planningChangesWarningCount} sous-produit(s)
              prêt(s) ont été décoché(s). Merci de refaire le point sur les produits déjà préparés.
            </Alert>
          )}
          <TableContainer component={Paper} sx={{ mb: 3 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Produit</TableCell>
                  <TableCell>Type</TableCell>
                  {!isDriver && (
                    <TableCell align="right" sx={{ bgcolor: "primary.main", color: "primary.contrastText" }}>Estimé</TableCell>
                  )}
                  <TableCell align="right" sx={{ bgcolor: "success.main", color: "success.contrastText" }}>Réel</TableCell>
                  {isDriver && (
                    <TableCell align="center" sx={{ width: 100 }}>Prêt</TableCell>
                  )}
                  {!isDriver && <TableCell align="right">Écart</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredProductRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={isDriver ? 4 : 5} align="center" sx={{ py: 3 }}>
                      {hasActiveFilters
                        ? "Aucun produit ne correspond aux filtres."
                        : isDriver
                          ? "Aucune commande sur cette période."
                          : "Aucun produit estimé ou commandé sur cette période."}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredProductRows.flatMap((row) => {
                    const estimatedNumeric = getEstimatedNumeric(
                      row.productId,
                      row.estimated,
                      localEstimates
                    );
                    const diff = row.real - estimatedNumeric;
                    const zoneRows = (productZoneRowsByProduct.get(row.productId) ?? [])
                      .filter((z) => z.totalQuantity > 0)
                      .sort((a, b) => {
                        const aNum = a.zoneNumber ?? Number.MAX_SAFE_INTEGER;
                        const bNum = b.zoneNumber ?? Number.MAX_SAFE_INTEGER;
                        if (aNum !== bNum) return aNum - bNum;
                        return a.zoneName.localeCompare(b.zoneName);
                      });
                    const zoneKeys = zoneRows.map((z) => `${z.productId}::${z.zoneId}`);
                    const allZonesChecked = zoneKeys.length > 0 && zoneKeys.every((k) => readyProductZoneKeys.has(k));
                    const someZonesChecked = zoneKeys.some((k) => readyProductZoneKeys.has(k));

                    const parentRow = (
                      <TableRow key={row.productId}>
                        <TableCell>{row.productName}</TableCell>
                        <TableCell>
                          <Chip size="small" label={row.typeLabel} variant="outlined" />
                        </TableCell>
                        {!isDriver && (
                          <TableCell align="right" sx={{ fontWeight: 600, color: "primary.main" }}>
                            <InputBase
                              type="number"
                              inputProps={{ min: 0, step: 1 }}
                              value={formatIntInputValue(estimatedNumeric)}
                              placeholder="Quantité"
                              onChange={(e) => {
                                const s = e.target.value;
                                if (s === "") {
                                  setLocalEstimates((prev) => ({ ...prev, [row.productId]: 0 }));
                                  return;
                                }
                                const parsed = Math.max(0, Math.floor(Number(s)));
                                if (!Number.isFinite(parsed)) return;
                                setLocalEstimates((prev) => ({ ...prev, [row.productId]: parsed }));
                              }}
                              onBlur={(e) => {
                                const raw = e.target.value.trim();
                                const v =
                                  raw === "" ? 0 : Math.max(0, Math.floor(Number(raw) || 0));
                                if (v !== row.estimated) handleSaveEstimated(row.productId, v);
                              }}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  const raw = (e.target as HTMLInputElement).value.trim();
                                  const v =
                                    raw === "" ? 0 : Math.max(0, Math.floor(Number(raw) || 0));
                                  if (v !== row.estimated) handleSaveEstimated(row.productId, v);
                                }
                              }}
                              sx={{
                                width: 56,
                                "& .MuiInputBase-input": {
                                  textAlign: "right",
                                  py: 0.5,
                                  px: 1,
                                  fontSize: "inherit",
                                  fontWeight: 600,
                                  color: "primary.main",
                                },
                              }}
                            />
                          </TableCell>
                        )}
                        <TableCell align="right" sx={{ fontWeight: 600, color: "success.main" }}>{row.real}</TableCell>
                        {isDriver && (
                          <TableCell align="center" padding="checkbox">
                            <Checkbox
                              checked={allZonesChecked}
                              indeterminate={!allZonesChecked && someZonesChecked}
                              onChange={() => toggleDriverReadyForProduct(row.productId)}
                              color="primary"
                              size="small"
                              inputProps={{ "aria-label": `Prêt : ${row.productName}` }}
                            />
                          </TableCell>
                        )}
                        {!isDriver && (
                          <TableCell align="right" sx={{ fontWeight: 600, color: diff >= 0 ? "success.main" : "error.main" }}>
                            {diff > 0 ? `+${diff}` : diff}
                          </TableCell>
                        )}
                      </TableRow>
                    );

                    const childRows = zoneRows.map((zone) => {
                      const zoneKey = `${zone.productId}::${zone.zoneId}`;
                      const zoneLabel = zone.zoneName;
                      return (
                        <TableRow key={zoneKey} sx={{ bgcolor: "grey.50" }}>
                          <TableCell sx={{ pl: 5, color: "text.secondary" }}>{zoneLabel}</TableCell>
                          <TableCell />
                          {!isDriver && <TableCell />}
                          <TableCell align="right" sx={{ fontWeight: 500 }}>{zone.totalQuantity}</TableCell>
                          {isDriver && (
                            <TableCell align="center" padding="checkbox">
                              <Checkbox
                                checked={readyProductZoneKeys.has(zoneKey)}
                                onChange={() => toggleDriverReadyForZone(zone.productId, zone.zoneId)}
                                color="primary"
                                size="small"
                                inputProps={{ "aria-label": `Prêt : ${row.productName} / ${zoneLabel}` }}
                              />
                            </TableCell>
                          )}
                          {!isDriver && <TableCell />}
                        </TableRow>
                      );
                    });

                    return [parentRow, ...childRows];
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Ingrédients nécessaires (masqué pour le rôle DRIVER) */}
          {!isDriver && (
            <>
              <Typography variant="h6" sx={{ mb: 1.5, display: "flex", alignItems: "center", gap: 1 }}>
                <Science /> Ingrédients nécessaires
              </Typography>
              <TableContainer component={Paper}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Ingrédient</TableCell>
                      <TableCell>Unité</TableCell>
                      <TableCell align="right">Quantité totale</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredIngredients.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={3} align="center" sx={{ py: 3 }}>
                          {hasActiveFilters
                            ? "Aucun ingrédient ne correspond aux filtres."
                            : "Aucun ingrédient (ou produits sans recette renseignée) sur cette période."}
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredIngredients.map((row) => {
                        const formatted = formatQuantityWithConversion(
                          row.totalQuantity,
                          row.unit
                        );
                        return (
                          <TableRow key={row.ingredientId}>
                            <TableCell>{row.ingredientName}</TableCell>
                            <TableCell>{formatted.unit}</TableCell>
                            <TableCell align="right">{formatted.value}</TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </>
      )}

      {!estimations && !isLoading && !isError && (
        <Typography color="text.secondary">
          Choisissez une période et cliquez sur Actualiser pour afficher les estimations.
        </Typography>
      )}
    </Box>
  );
}